import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card } from '../components/ui-components/Card';
import { Button } from '@/components/ui/button';
import { useBooking } from '../hooks/useBookingContext';
import { BookingProvider } from '../context/BookingContext';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import MeetingBasics from '../components/booking/MeetingBasics';
import Availability from '../components/booking/Availability';
import MeetingTypeAndPayment from '../components/booking/MeetingTypeAndPayment';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

const ProgressSteps: React.FC<{ currentStep: number }> = ({ currentStep }) => {
  return (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center">
        {[1, 2, 3].map((step) => (
          <React.Fragment key={step}>
            {step > 1 && (
              <div
                className={`w-16 h-0.5 ${
                  step <= currentStep ? 'bg-primary' : 'bg-border'
                } transition-colors duration-300`}
              ></div>
            )}
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                step < currentStep
                  ? 'bg-primary text-white'
                  : step === currentStep
                  ? 'bg-primary/20 text-primary border border-primary'
                  : 'bg-secondary text-muted-foreground'
              }`}
            >
              {step < currentStep ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                step
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const ContentWrapper: React.FC = () => {
  const { currentStep, setCurrentStep, currentBookingLink } = useBooking();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleNext = async () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      try {
        if (!user || !currentBookingLink) {
          toast.error("Unable to create booking link. Please try again.");
          return;
        }

        if (!user.id || typeof user.id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(user.id)) {
          toast.error("Invalid user session. Please log out and log in again.");
          return;
        }

        // Fetch the user's profile to get their username
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('username')
          .eq('id', user.id)
          .single();

        if (profileError || !profile?.username) {
          console.error("Error fetching profile:", profileError);
          toast.error("Unable to find your profile. Please complete your profile first.");
          return;
        }

        if (currentBookingLink.meetingTypes.inPerson && (!currentBookingLink.locations || currentBookingLink.locations.length === 0)) {
          toast.error("Please add at least one location for in-person meetings.");
          return;
        }

        const bookingLinkData = {
          name: currentBookingLink.name,
          duration: currentBookingLink.duration,
          availability: currentBookingLink.availability,
          user_id: user.id,
          username: profile.username, // Add the username from the profile
          locations: currentBookingLink.locations,
          meeting_types: currentBookingLink.meetingTypes
        };

        console.log('Creating booking link with data:', bookingLinkData);

        const { error } = await supabase
          .from('booking_links')
          .insert(bookingLinkData);

        if (error) {
          console.error("Error creating booking link:", error);
          toast.error("Failed to create booking link. Please try again.");
          return;
        }

        toast.success("Booking link created successfully!");
        navigate('/share-link');
      } catch (err) {
        console.error("Error in booking link creation:", err);
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/dashboard');
    }
  };

  const canProceed = () => {
    if (!currentBookingLink) return false;
    
    if (currentStep === 1) {
      return !!currentBookingLink.name;
    }
    
    if (currentStep === 2) {
      return Object.values(currentBookingLink.availability).some(day => day.enabled);
    }
    
    if (currentStep === 3) {
      return Object.values(currentBookingLink.meetingTypes).some(type => type);
    }
    
    return true;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <MeetingBasics />;
      case 2:
        return <Availability />;
      case 3:
        return <MeetingTypeAndPayment />;
      default:
        return <MeetingBasics />;
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 animate-fade-in">
      <h1 className="text-3xl font-bold text-center mb-4">Create Booking Link</h1>
      <p className="text-muted-foreground text-center mb-8">
        {currentStep === 1
          ? 'Let\'s start with the basics of your meeting'
          : currentStep === 2
          ? 'Set your availability for meetings'
          : 'Choose meeting types and payment settings'}
      </p>

      <ProgressSteps currentStep={currentStep} />

      <Card className="p-6 sm:p-8 mb-8 transition-all">
        {renderStepContent()}
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={handleBack} className="gap-2">
          <ArrowLeft className="h-5 w-5" />
          {currentStep === 1 ? 'Back to Dashboard' : 'Back'}
        </Button>
        <Button 
          onClick={handleNext} 
          className="gap-2"
          disabled={!canProceed()}
        >
          {currentStep === 3 ? 'Create Link' : 'Continue'}
          <ArrowRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

const CreateBookingLink: React.FC = () => {
  return (
    <DashboardLayout>
      <BookingProvider>
        <ContentWrapper />
      </BookingProvider>
    </DashboardLayout>
  );
};

export default CreateBookingLink;
